import { useEffect, useRef, useState } from 'react';
import type { HotelExpansion } from '@bandido/shared/types/hotels/hotel-search-progress';
import type { HotelSearchTransport } from './hotelSearchFlow';
export async function runHotelExpansion({
  parent,
  hotel,
  transport,
  signal,
  isCurrent,
  publish,
  pause,
}: {
  parent: string;
  hotel: string;
  transport: HotelSearchTransport;
  signal: AbortSignal;
  isCurrent: () => boolean;
  publish: (value: HotelExpansion) => void;
  pause: () => Promise<void>;
}) {
  const current = () => isCurrent() && !signal.aborted;
  const check = (result: HotelExpansion) => {
    if (result.parent_search_id !== parent || result.property_id !== hotel || result.known_results_search_id !== parent)
      throw new Error('Expansion scope');
    return result;
  };
  if (!current()) return;
  let result = check(await transport.expansion(parent, hotel, signal));
  if (!current()) return;
  if (result.status === 'not_requested') {
    result = check(await transport.expansion(parent, hotel, signal, true));
    if (!current()) return;
  }
  publish(result);
  while (['accepted', 'running'].includes(result.status)) {
    await pause();
    if (!current()) return;
    result = check(await transport.expansion(parent, hotel, signal));
    if (!current()) return;
    publish(result);
  }
}
/** Only opening a detail may enqueue work; pages and periodic reads never POST. */
export function useHotelExpansion(parent: string, hotel: string, transport: HotelSearchTransport) {
  const [state, setState] = useState<HotelExpansion | null>(null);
  const [error, setError] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    const id = ++generation.current,
      abort = new AbortController();
    setState(null);
    setError(false);
    const current = () => generation.current === id && !abort.signal.aborted;
    const deadline = setTimeout(() => {
      abort.abort();
      if (generation.current === id) setError(true);
    }, 120000);
    const pause = () =>
      new Promise<void>((resolve, reject) => {
        if (abort.signal.aborted) {
          reject(new Error('stopped'));
          return;
        }
        const cancel = () => {
          clearTimeout(timer);
          abort.signal.removeEventListener('abort', cancel);
          reject(new Error('stopped'));
        };
        const timer = setTimeout(() => {
          abort.signal.removeEventListener('abort', cancel);
          resolve();
        }, 1500);
        abort.signal.addEventListener('abort', cancel, { once: true });
      });
    void runHotelExpansion({
      parent,
      hotel,
      transport,
      signal: abort.signal,
      isCurrent: current,
      publish: setState,
      pause,
    })
      .catch(() => {
        if (current()) setError(true);
      })
      .finally(() => clearTimeout(deadline));
    return () => {
      generation.current++;
      abort.abort();
      clearTimeout(deadline);
    };
  }, [parent, hotel, transport]);
  return { state, error };
}
